import { Button, Grid, GridItem, Link, TextField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { useState } from 'react';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useGetDMRCase } from '../../../../hooks/useGetDMRInfo';
import CarInfoBox from './CarInfoBox';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { track } from '../../../../utils/trackingUtil';
import { useSetVehicleInheritanceFields } from '../../../../hooks/useSetVehicleInheritanceFields';

const validateRegNumber = (regNumber: string): boolean => {
    return /^[a-zA-Z0-9æøåÆØÅ]{2,7}$/.test(regNumber);
};

const RegistrationNumberStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { getDMRCase } = useGetDMRCase();
    const { setVehicleInheritanceFields } = useSetVehicleInheritanceFields();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const getHippoText = useHippoText();
    const { cars = [] } = coverables;
    const coverableIndex = getCoverableIndex(cars, currentProduct.fixedId);
    const coverable = cars[coverableIndex];
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [isLoading, setIsLoading] = useState(false);
    const [isDMRLoading, setIsDMRLoading] = useState(false);
    const isRegNumberValid = coverable.regNumber
        ? validateRegNumber(coverable.regNumber)
        : undefined;
    const [isVehicleFound, setIsVehicleFound] = useState<boolean | undefined>(
        coverable.regNumber ? true : undefined
    );

    const fetchDMRInformation = async (registrationNumber: string): Promise<void> => {
        await getDMRCase(registrationNumber).then((dmrData) => {
            if (dmrData?.model && dmrData?.make && dmrData?.variant) {
                updateCoverableInformation({
                    ...dmrData,
                    isAddedInOnsa: coverable.isAddedInOnsa
                });
                setIsVehicleFound(true);
            } else {
                setIsVehicleFound(false);
            }
            setIsDMRLoading(false);
        });
    };

    const onNext = (): void => {
        setIsLoading(true);
        updateCoverableInformation({
            name: `${coverable.make ?? ''} ${coverable.model ?? ''}, ${coverable.regNumber ?? ''}`
        });
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            car: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                regNumber: coverable.regNumber
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(async () => {
            track(`${currentProduct.flavourName}ContentCoverableInformationRegistrationNumber`, [
                {
                    registrationNumber: coverable.regNumber
                },
                {
                    engineSize: coverable.engineSize
                },
                {
                    make: coverable.make
                },
                {
                    model: coverable.model
                },
                {
                    variant: coverable.variant
                }
            ]);
            await setVehicleInheritanceFields();
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            hidePrevious
            disableNext={!isVehicleFound || isLoading || isDMRLoading || !isRegNumberValid}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate>
                <Grid>
                    <GridItem colStart={1} span={8}>
                        <p>
                            {getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-REGISTRATION-PLATE-HEADER'
                            )}
                        </p>
                        <TextField
                            label={getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-REGISTRATION-PLATE-HEADER-FIELD'
                            )}
                            value={coverable.regNumber ?? ''}
                            onChange={(event) => {
                                updateCoverableInformation({
                                    regNumber: event.target.value?.toUpperCase()
                                });
                            }}
                            errorText="Registreringsnr. er ikke gyldigt"
                            isValid={isRegNumberValid}
                            minLength={2}
                            maxLength={7}
                            disabled={isLoading || isDMRLoading}
                        />
                        <div className="u-display-flex@md u-flex-justify-between">
                            <Button
                                className="u-spacing-stack-m"
                                disabled={!isRegNumberValid || isLoading || isDMRLoading}
                                isLoading={isDMRLoading}
                                onClick={() => {
                                    setIsDMRLoading(true);
                                    void fetchDMRInformation(coverable.regNumber ?? '');
                                }}
                            >
                                {getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-REGISTRATION-PLATE-FIND-CAR-BUTTON'
                                )}
                            </Button>
                            <Link
                                theme="logo"
                                className={
                                    isLoading || isDMRLoading
                                        ? 'u-spacing-stack-top-6 u-color-supplement-grey-medium'
                                        : 'u-spacing-stack-top-6'
                                }
                                onClick={() => {
                                    if (isLoading || isDMRLoading) return;
                                    goNext();
                                }}
                            >
                                {getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-REGISTRATION-PLATE-UNKNOWN'
                                )}
                            </Link>
                        </div>
                    </GridItem>
                    <GridItem span={4}>
                        <CarInfoBox isVehicleFound={isVehicleFound} coverable={coverable} />
                    </GridItem>
                </Grid>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default RegistrationNumberStep;
